/**
 * React hook for persistent streaming connections (SSE).
 *
 * Manages the lifecycle of a {@link createStream} connection:
 * creates on mount / config change, closes on unmount.
 * Exposes connection state and lastEventId as reactive values.
 *
 * @example
 * ```tsx
 * const { state, lastEventId } = useStream<NotificationEvent>({
 *   url: notificationUrl,
 *   protocol: EStreamProtocol.SSE,
 *   auth: { getToken: () => getAccessTokenSilently() },
 *   observability: { module: 'notifications', operation: 'stream' },
 *   onMessage: (event) => handleNotification(event),
 * });
 * ```
 */

import { useEffect, useRef, useState } from 'react';

import {
  type IStreamConfig,
  type IStreamCallbacks,
  type IStreamConnection,
  type IStreamError,
  type IRetryInfo,
  type IMessageMeta,
  EStreamState,
} from '../types';
import { createStream } from '../stream-client';

/**
 * Combined config + callbacks for the useStream hook.
 * Callbacks are specified at the top level for ergonomic usage.
 */
export interface IUseStreamOptions<T> extends IStreamConfig<T> {
  /** Called for each parsed message from the stream. */
  onMessage: (data: T, meta: IMessageMeta) => void;
  /** Called when a non-recoverable or exhausted error occurs. */
  onError?: (streamError: IStreamError) => void;
  /** Called when the stream completes normally. */
  onComplete?: (results: T[]) => void;
  /** Called before each retry attempt. */
  onRetry?: (info: IRetryInfo) => void;
  /** Change this to force a fresh connection without changing the url. */
  reconnectKey?: string | number;
}

/**
 * Return value of the useStream hook.
 */
export interface IUseStreamReturn {
  /** Current connection state. */
  state: EStreamState;
  /** Last SSE event ID received (for external tracking). */
  lastEventId: string | undefined;
  /** Imperatively close the connection. */
  close: () => void;
}

/**
 * React hook for persistent streaming connections.
 *
 * Creates a stream connection that auto-reconnects on transient failures.
 * The connection is torn down on unmount or when the url changes.
 *
 * @param options - Stream configuration and callbacks. Pass `url: null` to skip connection.
 * @returns Reactive connection state
 */
export function useStream<T>(
  options: IUseStreamOptions<T> | null,
): IUseStreamReturn {
  const [state, setState] = useState<EStreamState>(EStreamState.IDLE);
  const [eventId, setEventId] = useState<string | undefined>();
  const connectionRef = useRef<IStreamConnection | null>(null);

  // Store callbacks in refs to avoid reconnecting when they change identity
  const onMessageRef = useRef(options?.onMessage);
  onMessageRef.current = options?.onMessage;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;
  const onRetryRef = useRef(options?.onRetry);
  onRetryRef.current = options?.onRetry;

  // eslint-disable-next-line no-restricted-syntax -- reason: Stream connection lifecycle (create on mount/URL change, close on unmount)
  useEffect(() => {
    if (!options?.url) {
      setState(EStreamState.IDLE);
      return;
    }

    const callbacks: IStreamCallbacks<T> = {
      onMessage: (data, meta) => {
        onMessageRef.current?.(data, meta);
        if (meta.eventId) {
          setEventId(meta.eventId);
        }
      },
      onStateChange: newState => {
        setState(newState);
      },
      onError: err => onErrorRef.current?.(err),
      onRetry: info => onRetryRef.current?.(info),
    };

    // Only opt into stream-client's results buffer when the caller actually
    // wants the array. For long-lived SSE this avoids retaining every event.
    if (options.onComplete) {
      callbacks.onComplete = results => onCompleteRef.current?.(results);
    }

    // Extract config without callback properties
    const {
      onMessage: _onMessage,
      onError: _onError,
      onComplete: _onComplete,
      onRetry: _onRetry,
      reconnectKey: _reconnectKey,
      ...streamConfig
    } = options;

    const connection = createStream<T>(streamConfig, callbacks);
    connectionRef.current = connection;

    return () => {
      connection.close();
      connectionRef.current = null;
    };
    // Reconnect when URL, protocol, or reconnectKey changes. Callbacks are
    // stabilized via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.url, options?.protocol, options?.reconnectKey]);

  const close = () => {
    connectionRef.current?.close();
    connectionRef.current = null;
  };

  return {
    state,
    lastEventId: eventId,
    close,
  };
}
